<template>
  <v-dialog
    :model-value="true"
    persistent
    scrollable
    max-width="630px"
  >
    <v-form ref="form">
      <v-card>
        <v-toolbar color="primary" dark class="pl-6">
          <div class="d-flex flex-column">
            <span class="text-h6">
              Gerenciar Leituras de Sócios
            </span>
            <span class="text-caption">
              {{ eventName(event) }}
            </span>
          </div>
        </v-toolbar>
        <v-card-text class="pb-0" style="height: auto">
          <v-btn
            class="text-capitalize px-0 mb-3"
            append-icon="fa fa-plus-square"
            variant="text"
            color="primary"
            density="compact"
            @click="add"
          >
            <h3>Adicionar Leitura</h3>
          </v-btn>
          <v-row v-for="reading, index in _readings" :key="index" class="d-flex justify-center ga-3">
            <v-col>
              <v-text-field
                v-model="reading.name"
                label="Nome do Plano"
                class="flex-grow-1 flex-shrink-0"
                density="compact"
                variant="outlined"
                color="orange lighten-2"
                hide-details="auto"
                :disabled="loader.loading"
                :rules="[(s: string) => s.length > 1 || 'O nome do plano não pode estar vazio.']"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="reading.quantity"
                label="Leituras"
                type="number"
                class="flex-grow-0 flex-shrink-1"
                density="compact"
                variant="outlined"
                color="orange lighten-2"
                hide-details="auto"
                :disabled="loader.loading"
                :rules="[(v: number) => v >= 0 || 'A quantidade não pode ser negativa.']"
                @update:model-value="v => reading.quantity = Number(v.replace(/[^0-9]/g, ''))"
              >
                <template #append>
                  <v-btn
                    icon
                    :disabled="loader.loading"
                    density="compact"
                    min-height="32"
                    min-width="32"
                    elevation="1"
                    color="error"
                    @click="remove(reading)"
                  >
                    <v-icon
                      icon="fa-solid fa-trash"
                      size="x-small"
                    />
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            variant="text"
            :disabled="loader.loading"
            @click="emit('close', readings)"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="green darken-1"
            variant="text"
            :disabled="loader.loading"
            @click="submit"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script setup lang="ts">
import { eventName } from '@/plugins/eventName'
import ReadingService from '@/services/ReadingService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Event from '@/types/Event'
import { OverviewSignatureItem } from '@/types/Overview'
import { onBeforeMount, ref, useTemplateRef } from 'vue'
import type { VForm } from 'vuetify/lib/components'

interface Props {
  event: Event
  readings: OverviewSignatureItem[]
}

const loader = useLoader()
const snackbar = useSnackBar()

const emit = defineEmits<{
  close: [ readings: OverviewSignatureItem[] ]
}>()

const form = useTemplateRef<VForm>('form')

const { event, readings } = defineProps<Props>()

const _readings = ref<OverviewSignatureItem[]>(JSON.parse(JSON.stringify(readings)))

function add() {
  _readings.value.unshift({ id: '', event_id: event.id, name: '', quantity: 0 })
}

function remove(reading: OverviewSignatureItem) {
  _readings.value = _readings.value.filter(r => r !== reading)
}

async function submit() {
  await form.value?.validate()

  if (!form.value?.isValid) return

  try {
    loader.increment()

    await (new ReadingService()).updateSignatureReadings(event.id, _readings.value)

    snackbar.show('Leituras atualizadas com sucesso!', 'success')

    emit('close', _readings.value)
  } finally {
    loader.decrement()
  }
}

onBeforeMount(() => {
  if (readings.length === 0) add()
})
</script>
